import { Link } from "react-router-dom";
import {
  EnvelopeSimple,
  Phone,
  LinkedinLogo,
  ArrowRight,
  ChatCircleText,
  Calendar,
  ShieldCheck,
  VideoCamera,
} from "@phosphor-icons/react";

const CALENDLY_URL = "https://calendly.com/rh-mpadvisory/30min";

export default function ContactUs() {
  return (
    <div data-testid="contact-us-page">
      {/* HERO */}
      <section className="bg-navy-deep text-white py-20 lg:py-24 relative noise-overlay">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/15">
            <span className="eyebrow text-purple-accent">Nous contacter</span>
          </div>
          <h1 className="mt-8 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
            Parlons de <span className="font-serif-italic text-purple-accent">votre projet</span>
          </h1>
          <p className="mt-6 text-white/75 max-w-2xl mx-auto leading-relaxed">
            Un échange direct, sans engagement, pour comprendre vos enjeux
            et imaginer ensemble la meilleure approche.
          </p>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="bg-body-bg py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left : info cards */}
            <div className="lg:col-span-7 space-y-6">
              <ContactCard
                icon={EnvelopeSimple}
                title="Email"
                lines={["rh@mpadvisory.fr"]}
                action={{ href: "mailto:rh@mpadvisory.fr", label: "Envoyer un email" }}
                testid="contact-card-email"
              />
              <ContactCard
                icon={Phone}
                title="Téléphone"
                lines={["06 83 17 51 27", "Lun. — Ven. · 9h00 — 18h00"]}
                action={{ href: "tel:+33683175127", label: "Appeler maintenant" }}
                testid="contact-card-phone"
              />
              <ContactCard
                icon={LinkedinLogo}
                title="LinkedIn"
                lines={["Magalie Pichon"]}
                action={{
                  href: "https://www.linkedin.com/in/magalie-pichon-25247a102/",
                  label: "Voir le profil",
                  external: true,
                }}
                testid="contact-card-linkedin"
              />
            </div>

            {/* Right : promise + CTA */}
            <div className="lg:col-span-5">
              <div className="bg-navy-deep text-white rounded-3xl p-10 lg:p-12 sticky top-32 noise-overlay relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-purple-accent/20 blur-3xl" />
                <div className="relative">
                  <p className="eyebrow text-purple-accent">Notre promesse</p>
                  <h2 className="mt-4 font-serif text-3xl leading-tight">
                    Une réponse <span className="font-serif-italic text-purple-accent">sous 24h</span> ouvrées
                  </h2>
                  <div className="accent-line mt-6" />
                  <ul className="mt-8 space-y-4 text-[15px] text-white/80">
                    <Bullet icon={ChatCircleText}>Un premier échange téléphonique de 30 minutes</Bullet>
                    <Bullet icon={Calendar}>Une recommandation d'approche personnalisée</Bullet>
                    <Bullet icon={ShieldCheck}>Une totale confidentialité, à toutes les étapes</Bullet>
                  </ul>
                  <Link
                    to="/formulaire"
                    data-testid="contactus-cta-form"
                    className="mt-10 inline-flex items-center gap-2 rounded-full bg-purple-accent hover:bg-purple-accent-hover text-white px-7 py-3.5 text-sm font-medium transition-all hover:-translate-y-0.5"
                  >
                    Remplir le formulaire
                    <ArrowRight size={16} weight="bold" />
                  </Link>

                  <div className="mt-6 pt-6 border-t border-white/10">
                    <a
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contactus-cta-meeting"
                      className="group inline-flex items-center gap-3 rounded-full bg-white/10 hover:bg-white/15 border border-purple-accent/40 hover:border-purple-accent text-white px-6 py-3 text-[14px] font-medium transition-all hover:-translate-y-0.5 backdrop-blur-sm"
                    >
                      <VideoCamera size={18} weight="duotone" className="text-purple-accent" />
                      Prendre RDV en visio 30 min
                      <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
                    </a>
                    <p className="mt-3 text-[13px] text-white/55 font-serif italic">
                      Échange confidentiel et sans engagement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactCard({ icon: Icon, title, lines, action, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-2xl p-8 border border-gray-100 shadow-subtle lift-on-hover flex items-start gap-6"
    >
      <div className="w-14 h-14 rounded-2xl bg-purple-soft flex items-center justify-center shrink-0">
        <Icon size={26} weight="duotone" className="text-purple-accent" />
      </div>
      <div className="flex-1">
        <p className="eyebrow text-purple-accent">{title}</p>
        <div className="mt-3 space-y-1">
          {lines.map((l, i) => (
            <p key={i} className="font-serif text-xl text-navy-deep">
              {l}
            </p>
          ))}
        </div>
        {action && (
          <a
            href={action.href}
            target={action.external ? "_blank" : undefined}
            rel={action.external ? "noopener noreferrer" : undefined}
            className="mt-5 inline-flex items-center gap-2 text-sm text-purple-accent hover:text-purple-accent-hover font-medium transition"
          >
            {action.label}
            <ArrowRight size={14} weight="bold" />
          </a>
        )}
      </div>
    </div>
  );
}

function Bullet({ icon: Icon, children }) {
  return (
    <li className="flex items-start gap-3">
      <Icon size={20} weight="duotone" className="text-purple-accent shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  );
}
